import { CampaignType } from "@adlywise/shared";
import Card from "@/components/common/Card";
import FillBar from "@/components/common/FillBar";
import { campaignBudgetUsed } from "@/utils/calculations";

const formatNumber = (n) => (Number(n) || 0).toLocaleString("en-US");
const formatCurrency = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function CampaignSummarySidebar({
  form,
  isEditMode,
  budget,
  budgetLoading,
  stats,
  statsLoading,
}) {
  const isEstimatedVisible =
    form.status === "draft" ||
    form.status === "active" ||
    form.status === "paused";
  const isActualVisible =
    form.status === "active" ||
    form.status === "paused" ||
    form.status === "completed";

  const isTransit = Number(form.type) === CampaignType.Transit;
  const impressions = Number(stats?.totalReceivers ?? 0);
  const confirmed = Number(stats?.confirmedImpressions ?? 0);
  const winRate =
    impressions > 0 ? Math.round((confirmed / impressions) * 100) : 0;
  const devices = Number(stats?.uniqueReceivers ?? 0);
  // Budget used = real ad_served spend (Σ buyPrice / 1000), same basis as the
  // income board — not impressions × cpm (0 for transit campaigns).
  const budgetUsed = campaignBudgetUsed(stats);
  const budgetCap = Number(form.budgetCap ?? 0);
  const impressionsCap = Number(form.impressionsCap ?? 0);
  const animated = form.status !== "completed";

  return (
    <Card className="flex flex-col gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-(--text-muted)">
          Campaign summary
        </p>
        <p className="mt-1 truncate text-base font-semibold text-(--text-primary)">
          {form.name || "Untitled campaign"}
        </p>
      </div>

      {isEditMode ? (
        <div className="flex flex-col gap-3 rounded-(--radius-md) bg-(--surface-2) p-4">
          <div>
            <p className="text-xs font-medium text-(--text-secondary)">
              Impressions
            </p>
            <p className="mt-0.5 text-lg font-bold text-(--text-primary)">
              {statsLoading ? (
                <span className="text-(--text-muted)">…</span>
              ) : (
                <>
                  {formatNumber(impressions)}
                  {impressionsCap > 0 && (
                    <span className="ml-1 text-[11.5px] font-medium text-(--text-muted)">
                      / {formatNumber(impressionsCap)}
                    </span>
                  )}
                </>
              )}
            </p>
            {!statsLoading && (
              <FillBar
                used={impressions}
                cap={impressionsCap}
                animated={animated}
              />
            )}
          </div>

          {isTransit && (
            <div>
              <p className="text-xs font-medium text-(--text-secondary)">
                Confirmed impressions
              </p>
              <p className="mt-0.5 text-lg font-bold text-(--text-primary)">
                {statsLoading ? (
                  <span className="text-(--text-muted)">…</span>
                ) : (
                  <>
                    {formatNumber(confirmed)}
                    <span className="ml-1 text-[11.5px] font-medium text-(--text-muted)">
                      / {formatNumber(impressions)} ({winRate}% win rate)
                    </span>
                  </>
                )}
              </p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-(--text-secondary)">
              Unique devices
            </p>
            <p className="mt-0.5 text-lg font-bold text-(--text-primary)">
              {statsLoading ? (
                <span className="text-(--text-muted)">…</span>
              ) : (
                formatNumber(devices)
              )}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-(--text-secondary)">
              Budget used
            </p>
            <p className="mt-0.5 text-lg font-bold text-(--text-primary)">
              {statsLoading ? (
                <span className="text-(--text-muted)">…</span>
              ) : (
                <>
                  {formatCurrency(budgetUsed)}
                  {budgetCap > 0 && (
                    <span className="ml-1 text-[11.5px] font-medium text-(--text-muted)">
                      / {formatCurrency(budgetCap)}
                    </span>
                  )}
                </>
              )}
            </p>
            {!statsLoading && (
              <FillBar used={budgetUsed} cap={budgetCap} animated={animated} />
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-(--radius-md) bg-(--surface-2) p-4">
          <p className="text-xs font-medium text-(--text-secondary)">
            Delivery metrics
          </p>
          <p className="mt-2 text-xs text-(--text-muted)">
            Save the campaign to see impressions, devices and budget usage.
          </p>
        </div>
      )}

      {isEditMode && (
        <div className="rounded-(--radius-md) bg-(--surface-2) p-4">
          <p className="text-xs font-medium text-(--text-secondary)">
            Estimated cost
          </p>
          {budgetLoading ? (
            <p className="mt-2 text-sm text-(--text-muted)">Calculating…</p>
          ) : (
            <dl className="mt-2 flex flex-col gap-1.5 text-sm">
              {isEstimatedVisible && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-(--text-secondary)">Estimated</dt>
                  <dd className="text-base font-bold text-(--text-primary)">
                    {budget?.estimated != null
                      ? budget.estimated.toLocaleString("en-US")
                      : "—"}
                  </dd>
                </div>
              )}
              {isActualVisible && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-(--text-secondary)">Actual</dt>
                  <dd className="text-base font-bold text-(--text-primary)">
                    {budget?.actual != null
                      ? budget.actual.toLocaleString("en-US")
                      : "—"}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </div>
      )}

      <dl className="flex flex-col gap-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-(--text-secondary)">Status</dt>
          <dd className="font-medium capitalize text-(--text-primary)">
            {form.status}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-(--text-secondary)">Audiences</dt>
          <dd className="font-medium text-(--text-primary)">
            {form.selectedSegments.length || "Everyone"}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-(--text-secondary)">Flight</dt>
          <dd className="text-right font-medium text-(--text-primary)">
            {form.startDate || form.endDate
              ? `${form.startDate || "…"} → ${form.endDate || "…"}`
              : "—"}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-(--text-secondary)">CPM</dt>
          <dd className="font-medium text-(--text-primary)">
            {form.cpm ? `$${form.cpm}` : "—"}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-(--text-secondary)">Budget cap</dt>
          <dd className="font-medium text-(--text-primary)">
            {form.budgetCap ? `$${form.budgetCap}` : "—"}
          </dd>
        </div>
      </dl>
    </Card>
  );
}
